// Stage 4A-2 + 4B + Stage AI-3:AI ブログ生成ページ(後端実 API 接続 / mock provider / WP draft 保存)。
//
// 三欄構成を維持(左:生成条件 / 中:プレビュー / 右:操作 / 下:生成ログ)。
// - 「記事を生成する」→ POST /api/ai-blog/generate-stream(SSE streaming, Stage AI-3) → preview 描画
//   旧 POST /api/ai-blog/generate(Stage 4A 一次性) は aiBlogApi.generate として残置。
// - 「WordPress に下書き保存」→ POST /api/ai-blog/save-wordpress-draft → 成功時 WP draft 詳細を新タブで開く
//
// UI 紀律(plan / task-stage4.md):
// - prompt template / purpose / length / tone の UI ラベルは日文、payload は英数安定コード
// - 「再生成」は本輪 disabled(Stage 4A-2、Stage 4C で評価)
// - fake connected badge / fake API usage badge は表示しない
// - token_usage が全 0(mock provider)なら「今後対応予定」を表示
// - AI 出力を HTML として評価せず、React の text node としてそのまま描画する(エスケープは React 標準)
// - ユーザー可視 error message は日文(backend が返したものをそのまま表示)
// - AI Blog 未提交草稿只保存在当前浏览器 localStorage，不走数据库
// - key 绑定 tenant_id + user_id，避免跨租户 / 跨账号串草稿
// - TTL = 24 小时；成功保存到 WordPress 或手动清零时清除

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Wand2,
  Send,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  X,
  Globe2,
  RefreshCw,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { ApiError } from "../../api/client";
import { aiBlogApi } from "../../api/aiBlog";
import { streamGenerate } from "../../api/aiBlog";
import { blogApi } from "../../api/blog";
import type {
  GenerateArticleRequest,
  GenerateArticleResponse,
  SaveDraftRequest,
  SaveDraftResponse,
} from "../../api/aiBlog";
import { useAuth } from "../../auth/AuthProvider";
import { BlogDraftEditor } from "../../components/blog/BlogDraftEditor";
import type {
  BlogDraftEditorSaveStatus,
  BlogDraftEditorValue,
} from "../../components/blog/BlogDraftEditor";
import { extractTitleFromBody, resolveSaveTitle } from "../../utils/blog";

// ---- UI ラベル ↔ payload コードの対照表 ----
// UI は日文(payload に英語コードを送る)

const PROMPT_TEMPLATES = [
  { code: "standard_blog", label: "標準ブログ記事" },
  { code: "seo_focused", label: "SEO重視記事" },
  { code: "store_attraction", label: "店舗集客記事" },
  { code: "announcement", label: "お知らせ記事" },
  { code: "recruitment", label: "採用向け記事" },
] as const;

const PURPOSES = [
  { code: "lead_generation", label: "リード獲得" },
  { code: "education", label: "読者教育" },
  { code: "brand", label: "ブランディング" },
  { code: "promotion", label: "プロモーション" },
] as const;

const LENGTHS = [
  { code: "short", label: "短い(800字)" },
  { code: "medium", label: "標準(1,500字)" },
  { code: "long", label: "長い(2,500字)" },
] as const;

const TONES = [
  { code: "professional", label: "フォーマル" },
  { code: "friendly", label: "フレンドリー" },
  { code: "story", label: "ストーリー" },
] as const;

// 5 つの prompt template ごとの「初期の追加指示」(textarea に表示する自然语言の雛形)。
// 構造化フィールド(keywords / 読者 / 文字数 / 文体 / CTA)は form 側で入力し、
// サーバー側で自動レンダリングされるため、ここには書かない。
// 本エリアは「追加指示・記事の方針」を自然文で書くだけ。
const DEFAULT_PROMPT_TEXT: Record<string, string> = {
  standard_blog:
    "専門用語を使いすぎず、初めて読む人にも分かりやすく説明してください。\n導入部分では読者の課題を明確にしてください。\n最後に自然な形で CTA へ誘導してください。",
  seo_focused:
    "主要キーワードをタイトル・導入・見出し・本文中に自然に含めてください。\nメタディスクリプションを意識した1行目の書き出しにしてください。",
  store_attraction:
    "来店する価値が伝わるよう、店舗の雰囲気や体験を具体的に描写してください。\n地域性のある言い回しを適度に取り入れてください。",
  announcement:
    "新サービス / お知らせのポイントを 3 つ以内で簡潔にまとめてください。\n利用開始の手順やお問い合わせ先まで明記してください。",
  recruitment:
    "職場の雰囲気と仕事の内容が求職者に伝わるよう、具体的なエピソードを交えてください。\n応募のハードルを下げる一文を最後に添えてください。",
};

// キーワード UI 入力 → backend payload(配列) へ正規化
// 対応区切り:英カンマ / 日文読点 / 全角カンマ / 半角スペース / 全角スペース / 改行
// (仕様 §12 と一致。後端 _split_keywords_string と同じ規則)
function parseKeywordsInput(text: string): string[] {
  return text
    .split(/[、，,\s　\n\r]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// 「再生成」ボタン disabled の tooltip
const REGENERATE_DISABLED_TITLE = "今後対応予定";

// localStorage に保存する AI ブログ状態の形
// (form state + result + savedDraftInfo + editorDraft + timestamp)
interface AiBlogPersistedState {
  templateCode: string;
  promptText: string;
  keywordsInput: string;
  purpose: string;
  length: string;
  target: string;
  cta: string;
  tone: string;
  result: GenerateArticleResponse | null;
  savedDraftInfo: SaveDraftResponse | null;
  templateExpanded: boolean;
  // Stage Editor-1:BlogDraftEditor の in-flight 編集内容 + save status
  editorDraft?: BlogDraftEditorValue;
  editorSaveStatus?: BlogDraftEditorSaveStatus;
  updatedAt: number;
}

const AI_BLOG_LOCAL_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

// localStorage のキー:
// tenant 単位 + user 単位で分離し、cross-tenant / cross-user 再利用を防ぐ。
// テナントが確定する前(ログイン直後や tenant 未確定)に書かないよう、
// tenant / user どちらも取れた場合のみ storage を使う。
function buildLocalDraftStorageKey(tenantId: string | null, userId: string | null): string | null {
  if (!tenantId || !userId) return null;
  return `ai_blog_local_draft:${tenantId}:${userId}`;
}

function loadPersistedState(key: string): AiBlogPersistedState | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    // 最低限の型ガード(壊れたデータは無視)
    if (typeof obj !== "object" || obj === null) return null;
    if (typeof obj.templateCode !== "string") return null;
    if (typeof obj.updatedAt !== "number") {
      localStorage.removeItem(key);
      return null;
    }
    if (Date.now() - obj.updatedAt > AI_BLOG_LOCAL_DRAFT_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return obj as AiBlogPersistedState;
  } catch {
    return null;
  }
}

function savePersistedState(key: string, state: AiBlogPersistedState): void {
  try {
    // 保存禁止キー(secret 系)は含めない設計上無いが、二重防御として
    // 文字列化前に除外チェック(現状フィールドは form / result のみ)。
    const safe = {
      templateCode: state.templateCode,
      promptText: state.promptText,
      keywordsInput: state.keywordsInput,
      purpose: state.purpose,
      length: state.length,
      target: state.target,
      cta: state.cta,
      tone: state.tone,
      result: state.result,
      savedDraftInfo: state.savedDraftInfo,
      templateExpanded: state.templateExpanded,
      // Stage Editor-1:in-flight editor state を永続化
      editorDraft: state.editorDraft,
      editorSaveStatus: state.editorSaveStatus,
      updatedAt: state.updatedAt,
    };
    localStorage.setItem(key, JSON.stringify(safe));
  } catch {
    // localStorage 容量超過 / アクセス拒否等。UX に影響させない。
  }
}

function clearPersistedState(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// 1 ページ本体
export const AiBlogPage = () => {
  // 認証情報(tenant / user の id は sessionStorage key に使う)
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const storageKey = useMemo(
    () => buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null),
    [tenant?.id, user?.id]
  );
  const hydratedStorageKeyRef = useRef<string | null>(null);

  // ---- 入力 state(全て string / list、payload 生成時に正規化)----
  // 初期値は localStorage から復元(なければ default)。
  // useState の初期化関数で 1 回だけ読む(マウント時の復元)。
  const [templateCode, setTemplateCode] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return PROMPT_TEMPLATES[0].code;
    return loadPersistedState(k)?.templateCode ?? PROMPT_TEMPLATES[0].code;
  });
  const [promptText, setPromptText] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return DEFAULT_PROMPT_TEXT[PROMPT_TEMPLATES[0].code];
    return loadPersistedState(k)?.promptText ?? DEFAULT_PROMPT_TEXT[PROMPT_TEMPLATES[0].code];
  });
  const [keywordsInput, setKeywordsInput] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return "AI ブログ 中小企業";
    return loadPersistedState(k)?.keywordsInput ?? "AI ブログ 中小企業";
  });
  const [purpose, setPurpose] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return PURPOSES[0].code;
    return loadPersistedState(k)?.purpose ?? PURPOSES[0].code;
  });
  const [length, setLength] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return LENGTHS[1].code;
    return loadPersistedState(k)?.length ?? LENGTHS[1].code;
  });
  const [target, setTarget] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return "中小企業のマーケティング担当者";
    return loadPersistedState(k)?.target ?? "中小企業のマーケティング担当者";
  });
  const [cta, setCta] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return "資料請求はこちら";
    return loadPersistedState(k)?.cta ?? "資料請求はこちら";
  });
  const [tone, setTone] = useState<string>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return TONES[0].code;
    return loadPersistedState(k)?.tone ?? TONES[0].code;
  });

  // ---- 結果 / 状態 ----
  const [result, setResult] = useState<GenerateArticleResponse | null>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return null;
    return loadPersistedState(k)?.result ?? null;
  });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<{
    code: string;
    message: string;
    details?: unknown;
  } | null>(null);
  // プロンプトテンプレート本文(textarea)のデフォルト折りたたみ状態
  const [templateExpanded, setTemplateExpanded] = useState<boolean>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return false;
    return loadPersistedState(k)?.templateExpanded ?? false;
  });
  const [titleCandidates, setTitleCandidates] = useState<string[]>([]);
  const [selectedTitleCandidate, setSelectedTitleCandidate] = useState<string>("");
  const [titleGenerating, setTitleGenerating] = useState<boolean>(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [titleInstruction, setTitleInstruction] = useState<string>("");
  const [titleSettingsOpen, setTitleSettingsOpen] = useState<boolean>(false);
  const titleRequestSeqRef = useRef(0);

  // Stage 4B:WordPress 下書き保存の状態
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [publishingPost, setPublishingPost] = useState<boolean>(false);
  const [savedDraft, setSavedDraft] = useState<SaveDraftResponse | null>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return null;
    return loadPersistedState(k)?.savedDraftInfo ?? null;
  });

  // Stage Editor-1:BlogDraftEditor の in-flight 編集内容 + 保存状態。
  // localStorage から復元(マウント時 1 回のみ)。
  // editorSaveStatus が "saving" のまま残っていた場合は
  // 復元時に "dirty" に降格(リフレッシュで中途半端な saving を残さない)。
  const [editorDraft, setEditorDraft] = useState<BlogDraftEditorValue>(() => {
    const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
    if (!k) return { title: "", body: "" };
    const persisted = loadPersistedState(k)?.editorDraft;
    if (
      persisted &&
      typeof persisted === "object" &&
      typeof persisted.title === "string" &&
      typeof persisted.body === "string"
    ) {
      return persisted;
    }
    return { title: "", body: "" };
  });
  const [editorSaveStatus, setEditorSaveStatus] =
    useState<BlogDraftEditorSaveStatus>(() => {
      const k = buildLocalDraftStorageKey(tenant?.id ?? null, user?.id ?? null);
      if (!k) return "idle";
      const persisted = loadPersistedState(k)?.editorSaveStatus;
      // saving は遷移中状態なので復元しない(古い saving を残さない)
      if (persisted === "saving") return "dirty";
      if (
        persisted === "idle" ||
        persisted === "dirty" ||
        persisted === "saved" ||
        persisted === "error"
      ) {
        return persisted;
      }
      return "idle";
    });

  // Stage AI-3:streaming 中の逐チャンク累積テキスト + ref(同期参照用)。
  // ref は done 時に final_text が空だった場合のフォールバックに使う(React state は closure stale)。
  const [streamedText, setStreamedText] = useState<string>("");
  const streamedTextRef = useRef<string>("");

  // Stage UX-1:BlogDraftEditor の現在の値を常に同期する ref。
  // onDone callback が stale closure に捕まるのを防ぐ。
  const editorDraftRef = useRef<BlogDraftEditorValue>(editorDraft);
  useEffect(() => {
    editorDraftRef.current = editorDraft;
  }, [editorDraft]);

  // ---- localStorage への永続化 ----
  // form state / result / savedDraft / editorDraft が変わるたびに書く。
  // storageKey 未確定時(未ログイン)は書かない。
  useEffect(() => {
    if (!storageKey) return;
    savePersistedState(storageKey, {
      templateCode,
      promptText,
      keywordsInput,
      purpose,
      length,
      target,
      cta,
      tone,
      result,
      savedDraftInfo: savedDraft,
      templateExpanded,
      editorDraft,
      editorSaveStatus,
      updatedAt: Date.now(),
    });
  }, [
    storageKey,
    templateCode, promptText, keywordsInput, purpose, length, target, cta, tone,
    result, savedDraft, templateExpanded,
    editorDraft, editorSaveStatus,
  ]);

  // login 後に tenant / user が遅れて入ってきても、同一ブラウザ内の草稿を 1 回だけ復元する。
  useEffect(() => {
    if (!storageKey) return;
    if (hydratedStorageKeyRef.current === storageKey) return;
    const persisted = loadPersistedState(storageKey);
    hydratedStorageKeyRef.current = storageKey;
    if (!persisted) return;

    setTemplateCode(persisted.templateCode);
    setPromptText(persisted.promptText);
    setKeywordsInput(persisted.keywordsInput);
    setPurpose(persisted.purpose);
    setLength(persisted.length);
    setTarget(persisted.target);
    setCta(persisted.cta);
    setTone(persisted.tone);
    setResult(persisted.result);
    setSavedDraft(persisted.savedDraftInfo);
    setTemplateExpanded(Boolean(persisted.templateExpanded));
    setEditorDraft(
      persisted.editorDraft &&
      typeof persisted.editorDraft.title === "string" &&
      typeof persisted.editorDraft.body === "string"
        ? persisted.editorDraft
        : { title: "", body: "" },
    );
    setEditorSaveStatus(
      persisted.editorSaveStatus === "saving"
        ? "dirty"
        : persisted.editorSaveStatus ?? "idle",
    );
  }, [storageKey]);

  // ---- 初期テンプレートへ戻す ----
  // prompt_text を現在選択中のテンプレートの default で上書き(永続化なし、state のみ)。
  const handleResetPrompt = () => {
    setPromptText(DEFAULT_PROMPT_TEXT[templateCode] ?? "");
  };

  // templateCode を変えた時、textarea を「初期テンプレートへ戻す」ボタン相当で更新
  const handleTemplateChange = (next: string) => {
    setTemplateCode(next);
    setPromptText(DEFAULT_PROMPT_TEXT[next] ?? "");
  };

  // payload 生成
  const buildPayload = (): GenerateArticleRequest => {
    const keywords = parseKeywordsInput(keywordsInput);
    const payload: GenerateArticleRequest = {
      keywords,
      prompt_template_id: templateCode as GenerateArticleRequest["prompt_template_id"],
    };
    if (purpose) payload.purpose = purpose as GenerateArticleRequest["purpose"];
    if (length) payload.length = length as GenerateArticleRequest["length"];
    if (tone) payload.tone = tone as GenerateArticleRequest["tone"];
    if (target.trim()) payload.target = target.trim();
    if (cta.trim()) payload.cta = cta.trim();
    // prompt_text はトリムして空文字なら送らない
    const trimmed = promptText.trim();
    if (trimmed.length > 0) payload.prompt_text = trimmed;
    return payload;
  };

  const getTemplateLabel = (code?: string) =>
    PROMPT_TEMPLATES.find((t) => t.code === code)?.label ?? code ?? "";

  const getPurposeLabel = (code?: string) =>
    PURPOSES.find((p) => p.code === code)?.label ?? code ?? "";

  const generateTitleCandidates = async (
    article: string,
    payload: GenerateArticleRequest,
    instruction: string,
  ) => {
    const body = article.trim();
    if (!body) return;
    const seq = titleRequestSeqRef.current + 1;
    titleRequestSeqRef.current = seq;
    setTitleGenerating(true);
    setTitleError(null);
    try {
      const data = await aiBlogApi.generateTitles({
        article: body,
        template: getTemplateLabel(payload.prompt_template_id),
        keyword: payload.keywords.join(" "),
        purpose: getPurposeLabel(payload.purpose),
        target: payload.target ?? "",
        instruction: instruction.trim(),
      });
      if (titleRequestSeqRef.current !== seq) return;
      const titles = data.titles.slice(0, 3);
      setTitleCandidates(titles);
      const first = titles[0] ?? "";
      setSelectedTitleCandidate(first);
      if (first) {
        setEditorDraft((prev) => ({ ...prev, title: first }));
        setEditorSaveStatus("dirty");
      }
    } catch (e) {
      if (titleRequestSeqRef.current !== seq) return;
      const message =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "タイトル生成に失敗しました。";
      setTitleError(message);
    } finally {
      if (titleRequestSeqRef.current === seq) {
        setTitleGenerating(false);
      }
    }
  };

  const handleRegenerateTitles = () => {
    const body = editorDraftRef.current.body || editorDraft.body;
    void generateTitleCandidates(body, buildPayload(), titleInstruction);
  };

  const handleSelectTitleCandidate = (title: string) => {
    setSelectedTitleCandidate(title);
    setEditorDraft((prev) => ({ ...prev, title }));
    setEditorSaveStatus("dirty");
  };

  // 「生成結果をクリア」ボタン:現在の result / savedDraft / editorDraft /
  // sessionStorage を全部消す。Stage Editor-1 で editorDraft / editorSaveStatus
  // も同時にクリアする。
  const handleClearPreview = () => {
    titleRequestSeqRef.current += 1;
    setResult(null);
    setSavedDraft(null);
    setError(null);
    setTitleCandidates([]);
    setSelectedTitleCandidate("");
    setTitleGenerating(false);
    setTitleError(null);
    setEditorDraft({ title: "", body: "" });
    setEditorSaveStatus("idle");
    if (storageKey) clearPersistedState(storageKey);
  };

  // 記事生成ボタン(Stage AI-3:streaming 化)
  // - 既定で POST /api/ai-blog/generate-stream を fetch + ReadableStream で読む。
  // - delta ごとに streamedText に append(逐次レンダリング = 打字機效果)。
  // - done 時に finalText || streamedTextRef.current を result.body_preview に入れる。
  //   → handleSaveAsDraft は触らず、result.body_preview を見るだけで動く。
  // - error / 非 200 は既存 error state で日文表示。
  // - refresh retry / fallback 切替 / cancel は MVP 範囲外。
  const handleGenerate = async () => {
    setSubmitting(true);
    setError(null);
    setResult(null);
    setStreamedText("");
    streamedTextRef.current = "";
    titleRequestSeqRef.current += 1;
    setTitleCandidates([]);
    setSelectedTitleCandidate("");
    setTitleGenerating(false);
    setTitleError(null);
    // Stage Editor-1:再生成時は editorDraft / editorSaveStatus もリセット
    setEditorDraft({ title: "", body: "" });
    setEditorSaveStatus("idle");
    try {
      const payload = buildPayload();
      await streamGenerate(
        payload,
        {
          onDelta: (text) => {
            // Stage UX-1:delta を streamedText / editorDraft.body 両方に
            // append する。functional setState で stale closure を回避。
            // streamedText は 他 UI 互換のために残置(将来的に削除可)。
            streamedTextRef.current = streamedTextRef.current + text;
            setStreamedText(streamedTextRef.current);
            setEditorDraft((prev) => ({ ...prev, body: prev.body + text }));
          },
          onDone: (status, finalText) => {
            // Stage UX-1:done 時の body 決定順序
            //  1) finalText が非空 → finalText
            //  2) editorDraftRef.current.body(逐 delta 累積分)
            //  3) streamedTextRef.current(後方互換)
            // title は ユーザー入力が空のときだけ body 先頭から抽出。
            const current = editorDraftRef.current;
            const accumulated = current.body || streamedTextRef.current;
            const body =
              finalText && finalText.length > 0 ? finalText : accumulated;
            const resp: GenerateArticleResponse = {
              generation_id:
                typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                  ? crypto.randomUUID()
                  : `gen-${Date.now()}`,
              title_candidates: [],
              selected_title: "",
              outline: [],
              body_preview: body,
              seo_score: 0,
              generation_steps: [],
              // MVP:usage は backend が転送しないので 0 固定(将来 usage イベント復活時に再設計)
              token_usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
              prompt_template_used: payload.prompt_template_id ?? "standard_blog",
              source: "manual_ai",
            };
            setResult(resp);
            // Stage Editor-1 + UX-1:BlogDraftEditor に final body を流し、
            // title はユーザー入力が空のときだけ body 先頭から抽出。
            setEditorDraft({
              title:
                current.title && current.title.length > 0
                  ? current.title
                  : extractTitleFromBody(body) ?? "",
              body,
            });
            setEditorSaveStatus("idle");
            void generateTitleCandidates(body, payload, titleInstruction);
            // status は現状未使用(将来 status="cancelled" 等で UI 切替用に残置)
            void status;
          },
          onError: (code, message) => {
            setError({
              code,
              message: `生成に失敗しました: ${message}`,
              details: null,
            });
          },
        },
        undefined
      );
    } catch (e) {
      // streamGenerate 自体が throw する経路は現状ほぼ無いが、防御として残す
      if (e instanceof ApiError) {
        setError({
          code: e.code,
          message: `生成に失敗しました: ${e.message}`,
          details: e.details,
        });
      } else if (e instanceof Error) {
        setError({
          code: "NETWORK_ERROR",
          message: "生成に失敗しました: 通信エラーが発生しました。しばらく経ってから再度お試しください。",
        });
      } else {
        setError({
          code: "UNKNOWN",
          message: "生成に失敗しました: 予期しないエラーが発生しました。",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Stage 4B:AI preview → WordPress draft 保存
  // - Stage Editor-1:body は editorDraft.body(編輯後)を送信、
  //   title は editorDraft.title 優先 → extractTitleFromBody → "AI生成記事" の
  //   3 段フォールバック(resolveSaveTitle 共有 util)。
  // - 成功時:savedDraftInfo を state に保存 + editorSaveStatus="saved"
  // - 失敗時:error state に日文 message + 英文 code + editorSaveStatus="error"
  //
  // 2026-07-06 P0 修正:
  // - title 三段 fallback(selected_title → body_preview 第一行 `# ` → 「AI生成記事」)
  // - body_preview 空なら API 呼ばずに即エラー表示(無駄な 400 を防ぐ)
  // - 保存失敗時は catch で明示的に「保存に失敗しました: {message}」+ error_code を表示

  const saveCurrentDraft = async (showAlert = true): Promise<SaveDraftResponse | null> => {
    if (savingDraft) {
      // 二重起動防止(button disabled が効かない状況でも多重 submit 回避)
      return null;
    }
    if (!result) {
      setError({
        code: "AUTH_INVALID_REQUEST",
        message: "先に「記事を生成する」を実行してください。",
      });
      return null;
    }
    // Stage UX-1:body は editorDraft.body(ユーザー編輯後)をそのまま送信。
    // result.body_preview への fallback は廃止 — body の単一情報源は BlogDraftEditor。
    // 空なら API 呼ばずに即エラー(無駄な 400 を防ぐ)。
    const bodyText = (editorDraft.body || "").trim();
    if (bodyText.length === 0) {
      setError({
        code: "AUTH_INVALID_REQUEST",
        message: "保存に失敗しました: 保存できる本文がありません。先に「記事を生成する」を実行してください。",
      });
      return null;
    }
    // Stage Editor-1:title 優先順位は editorDraft.title → extractTitleFromBody → "AI生成記事"。
    // 共有 util resolveSaveTitle がこの 3 段 fallback を行う。
    const title = resolveSaveTitle(editorDraft.title, editorDraft.body);
    setSavingDraft(true);
    setEditorSaveStatus("saving");
    setError(null);
    try {
      const data = await aiBlogApi.saveDraft({
        title,
        body: editorDraft.body,
        generation_id: result.generation_id,
        prompt_template_id: result.prompt_template_used as SaveDraftRequest["prompt_template_id"],
      });
      // レスポンスは {status, wordpress_post_id, link, title, source} の形。
      setSavedDraft(data);
      setEditorSaveStatus("saved");
      if (storageKey) clearPersistedState(storageKey);
      await blogApi.refreshPosts(true);
      // 2026-07-06: 成功時も window.alert で保底表示(ユーザーは page 上部操作ボタン
      // から見ており、error box と同じ位置の success box が見えない可能性があるため)
      if (showAlert) {
        window.alert(
          `WordPressに下書き保存しました\n` +
          `wordpress_post_id: ${data.wordpress_post_id}\n` +
          `link: ${data.link ?? "(なし)"}`
        );
      }
      return data;
    } catch (e) {
      // 2026-07-06: 保存に失敗しました: {日文 message} の message を表示
      // (生成 / 保存 2 種類のエラーを user-visible message で区別)
      setEditorSaveStatus("error");
      if (e instanceof ApiError) {
        setError({
          code: e.code,
          message: `保存に失敗しました: ${e.message}`,
          details: e.details,
        });
      } else if (e instanceof Error) {
        setError({
          code: "NETWORK_ERROR",
          message: "保存に失敗しました: 通信エラーが発生しました。しばらく経ってから再度お試しください。",
        });
      } else {
        // ここに来ることはない(JS 仕様上 catch(e) の e は常に Error)が、防御として
        // 安定日文 message を返す。
        setError({
          code: "SAVE_ERROR",
          message: "保存に失敗しました: WordPress 下書き保存中に予期しないエラーが発生しました。",
        });
      }
      return null;
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSaveAsDraft = async () => {
    await saveCurrentDraft(true);
  };

  const handlePublishToWordPress = async () => {
    if (publishingPost || savingDraft) return;
    if (!result || !editorDraft.body.trim()) {
      setError({
        code: "AUTH_INVALID_REQUEST",
        message: "公開に失敗しました: 先に「記事を生成する」を実行してください。",
      });
      return;
    }

    setPublishingPost(true);
    setError(null);
    try {
      const draft =
        savedDraft && editorSaveStatus !== "dirty"
          ? savedDraft
          : await saveCurrentDraft(false);
      if (!draft) return;
      const published = await blogApi.publishPost(draft.wordpress_post_id);
      await blogApi.refreshPosts(true);
      setSavedDraft({
        ...draft,
        status: published.status,
        link: published.link ?? draft.link,
        title: published.title || draft.title,
      });
      setEditorSaveStatus("saved");
      if (storageKey) clearPersistedState(storageKey);
      window.alert(
        `WordPressで公開しました\n` +
        `wordpress_post_id: ${draft.wordpress_post_id}\n` +
        `link: ${published.link ?? draft.link ?? "(なし)"}`
      );
    } catch (e) {
      setEditorSaveStatus("error");
      if (e instanceof ApiError) {
        setError({
          code: e.code,
          message: `公開に失敗しました: ${e.message}`,
          details: e.details,
        });
      } else if (e instanceof Error) {
        setError({
          code: "NETWORK_ERROR",
          message: "公開に失敗しました: 通信エラーが発生しました。しばらく経ってから再度お試しください。",
        });
      } else {
        setError({
          code: "PUBLISH_ERROR",
          message: "公開に失敗しました: WordPress 公開中に予期しないエラーが発生しました。",
        });
      }
    } finally {
      setPublishingPost(false);
    }
  };

  const titleAction = (
    <button
      type="button"
      className="ai-title-regen-btn"
      onClick={handleRegenerateTitles}
      disabled={titleGenerating || submitting || !editorDraft.body.trim()}
      title={
        !editorDraft.body.trim()
          ? "先に本文を生成してください"
          : "本文は再生成せず、タイトル候補だけを再生成します"
      }
    >
      <RefreshCw size={14} />
      タイトルを再生成
    </button>
  );

  const titleAddon = (
    <div className="ai-title-assist">
      <div className="ai-title-assist-head">
        <div className="ai-title-assist-label">
          <Sparkles size={14} />
          <span>AIタイトル候補</span>
        </div>
      </div>

      {titleGenerating ? (
        <div className="ai-title-loading">タイトル生成中...</div>
      ) : titleCandidates.length > 0 ? (
        <div className="ai-title-candidate-grid" role="radiogroup" aria-label="AIタイトル候補">
          {titleCandidates.map((title) => {
            const selected = selectedTitleCandidate === title;
            return (
              <button
                key={title}
                type="button"
                className={`ai-title-candidate-card ${selected ? "selected" : ""}`}
                onClick={() => handleSelectTitleCandidate(title)}
                role="radio"
                aria-checked={selected}
              >
                <span className="ai-title-radio" aria-hidden="true" />
                <span>{title}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="ai-title-empty">
          本文生成後にタイトル候補を自動生成します。
        </div>
      )}

      {titleError && (
        <div className="ai-title-error" role="alert">
          {titleError}
        </div>
      )}

      <button
        type="button"
        className="ai-title-settings-toggle"
        onClick={() => setTitleSettingsOpen((v) => !v)}
        aria-expanded={titleSettingsOpen}
      >
        {titleSettingsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        詳細設定
      </button>

      {titleSettingsOpen && (
        <div className="ai-title-settings">
          <label className="field-label">タイトル生成の追加指示（任意）</label>
          <textarea
            className="input ai-title-instruction"
            value={titleInstruction}
            onChange={(e) => setTitleInstruction(e.target.value)}
            maxLength={200}
            rows={3}
            placeholder="例：30文字以内、SEO重視、数字を含める、店舗集客向け"
          />
          <div className="ai-title-settings-footer">
            <span>{titleInstruction.length} / 200</span>
            <button
              type="button"
              className="ai-title-settings-regen"
              onClick={handleRegenerateTitles}
              disabled={titleGenerating || submitting || !editorDraft.body.trim()}
            >
              <RefreshCw size={13} />
              再生成
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="ai-blog-grid mb-18">
        {/* 左: 生成条件 */}
        <Card title="生成条件" subtitle="AIに伝えたい内容を入力します">
          {/* プロンプトテンプレート選択(デフォルト折りたたみ:下拉 + 简短説明のみ) */}
          <div className="field">
            <label className="field-label">
              プロンプトテンプレート
            </label>
            <select
              className="select"
              value={templateCode}
              onChange={(e) => handleTemplateChange(e.target.value)}
              disabled={submitting}
            >
              {PROMPT_TEMPLATES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
            <span className="field-hint">
              テンプレートの選択はサーバー側で生成方針として使われます。下の各項目を入力すると、サーバー側で自動的にプロンプトへ組み込まれます。
            </span>
            <div className="row gap-8 mt-8">
              <button
                type="button"
                className="ai-template-toggle"
                onClick={() => setTemplateExpanded((v) => !v)}
                disabled={submitting}
                aria-expanded={templateExpanded}
              >
                {templateExpanded ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                {templateExpanded ? "詳細編集を閉じる" : "詳細編集を開く"}
              </button>
            </div>
          </div>

          {/* 追加指示・記事の方針(編集可、永続化なし、折り込み時非表示) */}
          {templateExpanded && (
            <div className="field">
              <label className="field-label">追加指示・記事の方針</label>
              <textarea
                className="input ai-blog-prompt-textarea"
                rows={6}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                disabled={submitting}
                placeholder={DEFAULT_PROMPT_TEXT[templateCode] ?? ""}
              />
              <div className="row gap-8 mt-8">
                <Button
                  variant="secondary"
                  iconLeft={<RotateCcw size={12} />}
                  onClick={handleResetPrompt}
                  disabled={submitting}
                >
                  初期の追加指示に戻す
                </Button>
              </div>
              <span className="field-hint">
                必要に応じて、記事の書き方や注意点を追加できます。
              </span>
            </div>
          )}

          {/* キーワード */}
          <div className="field">
            <label className="field-label">
              キーワード <span className="field-label-required">必須</span>
            </label>
            <input
              className="input"
              value={keywordsInput}
              onChange={(e) => setKeywordsInput(e.target.value)}
              disabled={submitting}
              placeholder="例: AI ブログ 中小企業,SEO"
            />
            <span className="field-hint">カンマ・スペース・改行区切りで 1〜10 個</span>
          </div>

          {/* 記事の目的 */}
          <div className="field">
            <label className="field-label">記事の目的</label>
            <select
              className="select"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              disabled={submitting}
            >
              {PURPOSES.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {/* 文字数 */}
          <div className="field">
            <label className="field-label">文字数目安</label>
            <select
              className="select"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              disabled={submitting}
            >
              {LENGTHS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>

          {/* ターゲット */}
          <div className="field">
            <label className="field-label">ターゲット読者</label>
            <input
              className="input"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={submitting}
              maxLength={200}
              placeholder="例: 中小企業のマーケティング担当者"
            />
            <span className="field-hint">1〜200 文字</span>
          </div>

          {/* CTA */}
          <div className="field">
            <label className="field-label">CTA</label>
            <input
              className="input"
              value={cta}
              onChange={(e) => setCta(e.target.value)}
              disabled={submitting}
              maxLength={100}
              placeholder="例: 資料請求はこちら"
            />
            <span className="field-hint">1〜100 文字</span>
          </div>

          {/* トーン */}
          <div className="field">
            <label className="field-label">トーン</label>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {TONES.map((t) => {
                const selected = tone === t.code;
                return (
                  <button
                    key={t.code}
                    type="button"
                    className="tone-chip"
                    data-selected={selected ? "true" : "false"}
                    onClick={() => setTone(t.code)}
                    disabled={submitting}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 生成ボタン */}
          <Button
            full
            iconLeft={<Sparkles size={14} />}
            onClick={handleGenerate}
            disabled={submitting || parseKeywordsInput(keywordsInput).length === 0}
          >
            {submitting ? "生成中..." : "記事を生成する"}
          </Button>

          {/* エラー表示(ユーザー可視 message は日文、code は英字小字) */}
          {error && (
            <div className="ai-error-box mt-12" role="alert">
              <AlertCircle size={14} />
              <div className="ai-error-text">
                <div className="ai-error-message">保存に失敗しました: {error.message}</div>
                <div className="ai-error-code">error_code: {error.code}</div>
              </div>
            </div>
          )}

          {/* 保存成功時は user-visible summary メッセージ(エラーと独立) */}
          {savedDraft && !savingDraft && !error && (
            <div className="ai-success-box mt-12" role="status">
              <CheckCircle2 size={14} />
              <span>
                {savedDraft.status === "publish" ? "WordPressで公開しました" : "WordPressに下書き保存しました"}
              </span>
            </div>
          )}
        </Card>

        {/* 中: 生成結果プレビュー */}
        <Card
          title="生成結果プレビュー"
          subtitle={
            // Stage AI-3:result 有时显示"完了",streaming 中显示"生成中",无数据显示引导
            submitting
              ? "AI 記事を生成しています。ストリーミングで逐次表示されます"
              : result
              ? "生成された内容を確認し、必要に応じて編集できます"
              : "「記事を生成する」を押すと、ここに結果が表示されます"
          }
          actions={
            result ? (
              <Badge color="green" dot>
                生成完了
              </Badge>
            ) : submitting ? (
              <Badge color="blue" dot>
                生成中
              </Badge>
            ) : null
          }
        >
          {/* Stage UX-1:本文プレビュー 只読 box は削除。BlogDraftEditor が単一の
              本文エリアとして常に表示される。
              - 生成前 / 空 body + !submitting:占位文案「生成すると、ここに編集可能な本文が表示されます」
              - 生成中:BlogDraftEditor readOnly、delta を editorDraft.body に逐次 append
              - 生成完了:BlogDraftEditor readOnly=false、ユーザー編集可 */}
          <div className="mb-18">
            {!submitting && editorDraft.body.length === 0 && (
              <div className="ai-empty-box">
                生成すると、ここに編集可能な本文が表示されます
              </div>
            )}
            <BlogDraftEditor
              value={editorDraft}
              onChange={(next) => {
                setEditorDraft(next);
                setEditorSaveStatus("dirty");
              }}
              mode="create"
              readOnly={submitting}
              isStreaming={submitting}
              saveStatus={editorSaveStatus}
              className="blog-draft-editor--large"
              titleAction={titleAction}
              titleAddon={titleAddon}
            />
          </div>

          {/* タイトル案 / 見出し構成 — 構造化フィールド(result 必要)は done 後のみ表示 */}
          {result && (
            <>
              {/* タイトル案 */}
              {result.title_candidates.length > 0 && (
              <div className="mb-18">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6b7280",
                    marginBottom: 8,
                  }}
                >
                  タイトル案 ({result.title_candidates.length}件)
                </div>
                {result.title_candidates.map((t, i) => (
                  <div
                    key={i}
                    className={`ai-headline-card ${i === 0 ? "selected" : ""}`}
                  >
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: i === 0 ? "#2563eb" : "#e5e7eb",
                          color: i === 0 ? "#fff" : "#6b7280",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {i === 0 ? <CheckCircle2 size={12} /> : i + 1}
                      </span>
                      <span style={{ fontWeight: 600, color: "#111827" }}>{t}</span>
                    </div>
                  </div>
                ))}
              </div>
              )}

              {/* 見出し構成 */}
              <div className="mb-18">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6b7280",
                    marginBottom: 8,
                  }}
                >
                  見出し構成
                </div>
                <div className="ai-outline-list">
                  <ol>
                    {result.outline.map((o, i) => (
                      <li key={i}>
                        <strong>{o.h2}</strong>
                        {o.h3.length > 0 && (
                          <ul className="ai-outline-sub">
                            {o.h3.map((sub, j) => (
                              <li key={j}>{sub}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              {/* 本文プレビュー(Stage AI-3):result 有無に関わらず上の streaming ブロックで表示済み。 */}

              {/* SEO スコア */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6b7280",
                    marginBottom: 8,
                  }}
                >
                  SEOスコア
                </div>
                <div className="progress-row">
                  <div className="progress-row-label">
                    <span>総合スコア</span>
                    <span style={{ color: "#111827", fontWeight: 700 }}>
                      {result.seo_score} / 100
                    </span>
                  </div>
                  <div className="progress" style={{ width: 160 }}>
                    <div
                      className={`progress-bar ${
                        result.seo_score < 70 ? "yellow" : "green"
                      }`}
                      style={{ width: `${Math.max(0, Math.min(100, result.seo_score))}%` }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* 右: 操作 */}
        <Card title="操作">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* 「再生成」ボタン:本輪は disabled(Stage 4A-2 不実装,Stage 4C 評価) */}
            <Button
              full
              iconLeft={<Wand2 size={14} />}
              disabled
              title={REGENERATE_DISABLED_TITLE}
            >
              本文を再生成
            </Button>

            <Button
              full
              variant="primary"
              iconLeft={<Globe2 size={14} />}
              onClick={handlePublishToWordPress}
              disabled={
                publishingPost ||
                savingDraft ||
                submitting ||
                !result ||
                !editorDraft.body.trim() ||
                savedDraft?.status === "publish"
              }
              title={
                submitting
                  ? "生成中です。完了までお待ちください"
                  : publishingPost
                    ? "公開中..."
                    : savedDraft?.status === "publish"
                      ? "すでに公開済みです"
                      : !result || !editorDraft.body.trim()
                        ? "先に「記事を生成する」を実行してください"
                        : "WordPress に下書き保存してから公開し、ブログ一覧を更新します"
              }
            >
              {publishingPost
                ? "WordPressに公開中..."
                : savedDraft?.status === "publish"
                  ? "WordPress公開済み"
                  : "WordPressに公開"}
            </Button>

            {/* 「WordPressに下書き保存」ボタン:Stage 4B で実体化
                Stage UX-1:disabled 条件に submitting / 空 body を追加。
                BlogDraftEditor の editorDraft.body を直接見る。 */}
            <Button
              full
              variant="secondary"
              iconLeft={<Send size={14} />}
              onClick={handleSaveAsDraft}
              disabled={
                savingDraft ||
                publishingPost ||
                submitting ||
                !result ||
                !editorDraft.body.trim() ||
                savedDraft?.status === "publish"
              }
              title={
                submitting
                  ? "生成中です。完了までお待ちください"
                  : savingDraft
                    ? "保存中..."
                    : savedDraft?.status === "publish"
                      ? "すでに公開済みです"
                      : !result || !editorDraft.body.trim()
                        ? "先に「記事を生成する」を実行してください"
                        : "AI 生成内容を WordPress の下書きとして保存します"
              }
            >
              {savingDraft
                ? "WordPressに下書き保存中..."
                : savedDraft?.status === "publish"
                  ? "WordPress公開済み"
                  : savedDraft
                    ? editorSaveStatus === "dirty"
                      ? "変更を下書き保存"
                      : "WordPressに下書き保存しました"
                    : "WordPressに下書き保存"}
            </Button>

            {/* 保存成功信息:WordPress post id / link / ブログ管理で確認 ボタン / クリアボタン
                2026-07-06:右側操作欄に合わせた縦並びレイアウト(full width) */}
            {savedDraft && (
              <div className="ai-saved-card">
                <div className="ai-saved-label">
                  {savedDraft.status === "publish" ? "WordPress 公開済み" : "WordPress 下書き保存済み"}
                </div>
                <div className="ai-saved-meta">
                  WordPress post id: <strong>{savedDraft.wordpress_post_id}</strong> / status: {savedDraft.status}
                </div>
                {savedDraft.link && (
                  <a
                    href={savedDraft.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ai-saved-link"
                  >
                    {savedDraft.link}
                  </a>
                )}
                <div className="ai-saved-actions">
                  <Button
                    full
                    variant="primary"
                    iconLeft={<Send size={12} />}
                    onClick={() => navigate("/blog")}
                  >
                    ブログ管理で確認
                  </Button>
                  <Button
                    full
                    variant="secondary"
                    iconLeft={<X size={12} />}
                    onClick={handleClearPreview}
                  >
                    生成結果をクリア
                  </Button>
                </div>
              </div>
            )}

            {/* 生成結果があるのに savedDraft がまだ無い時はクリアボタン(結果だけ消す)を单独表示 */}
            {result && !savedDraft && (
              <Button
                full
                variant="ghost"
                iconLeft={<X size={12} />}
                onClick={handleClearPreview}
                disabled={submitting || savingDraft}
                title="現在の生成結果とセッション状態をクリアします"
              >
                生成結果をクリア
              </Button>
            )}

          </div>

      </Card>
    </div>
  </div>
  );
};
